"use client";

import SigninForm from "@/app/(auth)/signin/signin-form";
import SignupForm from "@/app/(auth)/signup/signup-form";
import { AuthAction } from "@/enums/auth-actions";
import { useQueryState } from "nuqs";
import { startTransition, useEffect, useState } from "react";
import { useAuth } from "../auth-provider";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Segmented } from "../ui/segmented";

function AuthenticationFormModal() {
  const [authTab, setAuthTab] = useQueryState("auth", {
    defaultValue: "",
    clearOnDefault: true,
  });

  const { claims } = useAuth();

  useEffect(() => {
    if (claims && !claims.is_anonymous && authTab) {
      void setAuthTab(null);
    }
  }, [claims, authTab, setAuthTab]);

  const open = authTab === AuthAction.SIGNIN || authTab === AuthAction.SIGNUP;

  const onClose = (isOpen: boolean = false) => {
    if (!isOpen) {
      void setAuthTab(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="w-full gap-0 px-5 pt-14 pb-6 sm:max-w-sm sm:px-10 sm:py-14">
        <DialogTitle className="sr-only">
          {authTab === AuthAction.SIGNUP
            ? "Create Free Account"
            : authTab === AuthAction.SIGNIN
              ? "Sign In"
              : "Authentication"}
        </DialogTitle>
        <Segmented
          block
          options={[
            { label: "Create Free Account", value: AuthAction.SIGNUP },
            { label: "Sign In", value: AuthAction.SIGNIN },
          ]}
          value={authTab}
          onChange={setAuthTab}
          className="mb-8"
        />

        <AuthenticationFormModalContent
          authTab={authTab as AuthAction}
          after={() => onClose()}
        />
      </DialogContent>
    </Dialog>
  );
}

export default AuthenticationFormModal;

function AuthenticationFormModalContent({
  authTab,
  after,
}: {
  authTab: AuthAction;
  after: () => void;
}) {
  const [tab, setTab] = useState<AuthAction>(authTab);

  useEffect(() => {
    if (authTab) {
      startTransition(() => {
        setTab(authTab);
      });
    }
  }, [authTab]);

  return (
    <>
      {tab === AuthAction.SIGNIN && (
        <SigninForm key={tab} afterSignin={() => after()} />
      )}
      {tab === AuthAction.SIGNUP && (
        <SignupForm key={tab} afterSignup={() => after()} />
      )}
    </>
  );
}
